import React, { useState } from 'react';
import { ExpandMoreOutlined, ExpandLessOutlined } from '../../icons';
import { getPercentage } from '../../utils/resultSummary';
import '../../dashboard/MyMocksPage.css';

const getProgressClass = (percentage) => {
  if (percentage <= 30) return 'low';
  if (percentage <= 69) return 'medium';
  return 'high';
};

// Painel "Desempenho por matéria" (com breakdown por tópico ao expandir), compartilhado
// entre a aba Estatísticas (MyMocks.js) e a tela de resultado do simulado (SuccessPage.js).
const SubjectPerformancePanel = ({
  data,
  title = 'Desempenho por matéria',
  emptyMessage = 'Ainda não há respostas registradas para gerar estatísticas.',
}) => {
  const [expandedSubjects, setExpandedSubjects] = useState(() => new Set());

  const toggleSubjectTopics = (materia) => {
    setExpandedSubjects((prev) => {
      const next = new Set(prev);
      if (next.has(materia)) {
        next.delete(materia);
      } else {
        next.add(materia);
      }
      return next;
    });
  };

  return (
    <div className="stats-panel">
      <div className="stats-panel-header-row">
        {title && <h3>{title}</h3>}
        {data.length > 0 && <span className="stats-panel-total">{data.length} matérias</span>}
      </div>
      {data.length > 0 ? (
        <div className="stat-list">
          {data.map((item) => {
            const hasTopicBreakdown = (item.topicos || []).some((t) => t.topicoId != null);
            const isExpanded = expandedSubjects.has(item.materia);
            const percentage = getPercentage(item.acertos, item.total);
            return (
              <div key={item.materia} className="stat-item">
                <div
                  className={`stat-row-header${hasTopicBreakdown ? ' stat-header--clickable' : ''}`}
                  onClick={hasTopicBreakdown ? () => toggleSubjectTopics(item.materia) : undefined}
                  role={hasTopicBreakdown ? 'button' : undefined}
                  tabIndex={hasTopicBreakdown ? 0 : undefined}
                >
                  <span className="stat-row-toggle">
                    {hasTopicBreakdown && (
                      isExpanded ? (
                        <ExpandLessOutlined style={{ fontSize: 16 }} />
                      ) : (
                        <ExpandMoreOutlined style={{ fontSize: 16 }} />
                      )
                    )}
                  </span>
                  <span className="stat-row-name">{item.materia}</span>
                  <span className="stat-row-count">{item.acertos} de {item.total}</span>
                  <strong className={`stat-row-pct ${getProgressClass(percentage)}`}>{percentage}%</strong>
                </div>
                <div className="stat-bar-track stat-bar-track--flush">
                  <div
                    className={`stat-bar-fill ${getProgressClass(percentage)}`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                  />
                </div>

                {hasTopicBreakdown && isExpanded && (
                  <div className="stat-topic-list">
                    {item.topicos.map((topico) => {
                      const topicPercentage = getPercentage(topico.acertos, topico.total);
                      return (
                        <div key={topico.topicoId ?? 'sem-topico'} className="stat-topic-row">
                          <span className="stat-topic-name">{topico.topico}</span>
                          <span className="stat-topic-count">{topico.acertos} de {topico.total}</span>
                          <div className="stat-topic-bar-track">
                            <div
                              className={`stat-bar-fill ${getProgressClass(topicPercentage)}`}
                              style={{ width: `${Math.min(topicPercentage, 100)}%` }}
                            />
                          </div>
                          <span className={`stat-topic-pct ${getProgressClass(topicPercentage)}`}>{topicPercentage}%</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">{emptyMessage}</div>
      )}
    </div>
  );
};

export default SubjectPerformancePanel;
